import React, { useState, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import'./AmanagerDashbord.css'
import api from "../services/api";


ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// API URL for the manager's assigned forms
const API_MANAGER_FORMS_URL = api.defaults.baseURL + "/manager/assigned-forms";

// --- Reusable Sub-Components ---

const KpiCard = ({ title, value, icon, description }) => (
  <div className="kpi-card">
    <div className="kpi-icon">{icon}</div>
    <div className="kpi-content">
      <div className="kpi-value">{value}</div>
      <div className="kpi-title">{title}</div>
      <p className="kpi-description">{description}</p>
    </div>
  </div>
);

const BrokerPerformanceChart = ({ performanceData }) => {
  const chartData = {
    labels: performanceData.map((p) => p.name),
    datasets: [
      {
        label: "Total Submitted Value ($)",
        data: performanceData.map((p) => p.totalValue),
        backgroundColor: "rgba(33, 150, 243, 0.6)",
        borderColor: "rgba(33, 150, 243, 1)",
        borderWidth: 1,
      },
    ],
  };
  const options = {
    indexAxis: "y", // Horizontal bar chart
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Broker Performance by Submitted Value" },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) =>
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              notation: "compact",
            }).format(value),
        },
      },
    },
  };
  return <Bar data={chartData} options={options} />;
};

const AmanagerDashboard = () => {
      const [forms, setForms] = useState([]);
      const [isLoading, setIsLoading] = useState(true);
      const [error, setError] = useState(null);
      // --- NEW: State for search and filtering ---
      const [searchTerm, setSearchTerm] = useState("");
      const [activeStatus, setActiveStatus] = useState("all");
    
      // Get manager's name from localStorage
      const managerName = useMemo(() => {
        try {
          return JSON.parse(localStorage.getItem("user"))?.name || "Manager";
        } catch {
          return "Manager";
        }
      }, []);
    
      useEffect(() => {
        const fetchManagerForms = async () => {
          setIsLoading(true);
          setError(null);
          const token = localStorage.getItem("authToken");
          if (!token) {
            setError("Authentication Error: Please log in again.");
            setIsLoading(false);
            return;
          }
          try {
            const response = await fetch(API_MANAGER_FORMS_URL, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error(`Server Error: ${response.status}`);
            const data = await response.json();
            setForms(data);
          } catch (e) {
            setError(e.message);
          } finally {
            setIsLoading(false);
          }
        };
        fetchManagerForms();
      }, []);
    
      // --- Data Processing for Analytics ---
      const analytics = useMemo(() => {
        if (forms.length === 0)
          return {
            totalSubmittedValue: 0,
            pendingCount: 0,
            submittedCount: 0,
            finalizedCount: 0, // NEW
            brokerPerformance: [],
          };
    
        const submittedForms = forms.filter((f) => f.status === "submitted");
        const pendingForms = forms.filter((f) => f.status === "pending");
        // --- NEW: Filter for finalized forms ---
        const finalizedForms = forms.filter((f) => f.status === "finalized");
    
        // Correctly calculate totalSubmittedValue by looking inside the latest submission
        const totalSubmittedValue = submittedForms.reduce((sum, form) => {
          const latestSubmission = form.submissions?.[form.submissions.length - 1];
          const grandTotal = latestSubmission?.formData?.grandTotal || 0;
          return sum + grandTotal;
        }, 0);
    
        // Group data by broker for the performance chart/table
        const performanceMap = submittedForms.reduce((acc, form) => {
          const brokerId = form.brokerUserId?._id;
          if (!brokerId) return acc;
    
          const brokerName = form.brokerUserId.name || "Unknown Broker";
          
          const latestSubmission = form.submissions?.[form.submissions.length - 1];
          const grandTotal = latestSubmission?.formData?.grandTotal || 0;
    
          if (!acc[brokerId]) {
            acc[brokerId] = { name: brokerName, totalValue: 0, submissionCount: 0 };
          }
          
          acc[brokerId].totalValue += grandTotal;
          acc[brokerId].submissionCount += 1;
          return acc;
        }, {});
    
        const brokerPerformance = Object.values(performanceMap).sort(
          (a, b) => b.totalValue - a.totalValue
        );
    
        return {
          totalSubmittedValue,
          pendingCount: pendingForms.length,
          submittedCount: submittedForms.length,
          finalizedCount: finalizedForms.length, // NEW
          brokerPerformance,
        };
    }, [forms]);

    // --- NEW: Logic to filter forms based on search and status ---
    const filteredForms = useMemo(() => {
        return forms
          .filter((form) => {
            // Filter by status button
            if (activeStatus === "all") return true;
            return form.status === activeStatus;
          })
          .filter((form) => {
            // Filter by search term
            const brokerName = form.brokerUserId?.name || "";
            const invoiceId = form.baseInvoiceId || "";
            const searchTermLower = searchTerm.toLowerCase();
    
            return (
              brokerName.toLowerCase().includes(searchTermLower) ||
              invoiceId.toLowerCase().includes(searchTermLower)
            );
          });
      }, [forms, searchTerm, activeStatus]);
    
      if (isLoading)
  return (
     <div className="manager-dashboard-pages">
            <div className="card">
              <h2>Loading Dashboard...</h2>
            </div>
          </div>
        );
      if (error)
        return (
          <div className="manager-dashboard-pages">
            <div className="card error-card">
              <h2>Error</h2>
              <p>{error}</p>
            </div>
          </div>
        );
    
  return (
     <div className="manager-dashboard-pages">
      <header className="dashboard-header">
        <h1>Manager Dashboard</h1>
        <p>
          Welcome back, {managerName}! Here's the latest activity from your
          team.
        </p>
      </header>

      <section className="kpi-grid">
        <KpiCard
          title="Total Submitted Value"
          value={new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(analytics.totalSubmittedValue)}
          icon="💰"
          description={`From ${analytics.submittedCount} submitted forms`}
        />
        <KpiCard
          title="Forms Assigned"
          value={forms.length}
          icon="📄"
          description="Total forms sent to brokers"
        />
        <KpiCard
          title="Pending Submissions"
          value={analytics.pendingCount}
          icon="⏳"
          description="Forms awaiting broker action"
        />
        <KpiCard
          title="Active Brokers"
          value={analytics.brokerPerformance.length}
          icon="👥"
          description="Brokers with submitted forms"
        />
          <KpiCard
          title="Total Finalized Forms"
          value={analytics.finalizedCount}
          icon="✔️"
          description="Forms that have been completed"
        />
      </section>

      <main className="dashboard-layout">
        <div className="dashboard-main-content">
          <div className="card">
            {/* --- NEW: Table Header with Search and Filters --- */}
            <div className="table-header">
              <h3 className="card-title">All Assigned Forms</h3>
              <div className="table-controls">
                <input
                  type="text"
                  placeholder="Search by Broker or Invoice..."
                  className="search-bar"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="filter-buttons">
                  <button onClick={() => setActiveStatus('all')} className={activeStatus === 'all' ? 'active' : ''}>All</button>
                  <button onClick={() => setActiveStatus('pending')} className={activeStatus === 'pending' ? 'active' : ''}>Pending</button>
                  <button onClick={() => setActiveStatus('submitted')} className={activeStatus === 'submitted' ? 'active' : ''}>Submitted</button>
                  <button onClick={() => setActiveStatus('finalized')} className={activeStatus === 'finalized' ? 'active' : ''}>Finalized</button>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Broker</th>
                    <th>Status</th>
                    <th>Invoice #</th>
                    <th>Value</th>
                    <th>Assigned On</th>
                  </tr>
                </thead>
                <tbody>
                  {/* --- MODIFIED: Map over filteredForms instead of forms --- */}
                  {filteredForms.map((form) => {
                    const latestSubmission =
                      form.submissions?.[form.submissions.length - 1];

                    const grandTotal =
                      latestSubmission?.formData?.grandTotal || 0;

                    return (
                      <tr key={form._id}>
                        <td>{form.brokerUserId?.name || "N/A"}</td>
                        <td>
                          <span className={`badge status-${form.status}`}>
                            {form.status}
                          </span>
                        </td>
                        <td>{form.baseInvoiceId || "---"}</td>
                        <td>
                          {form.status === "submitted"
                            ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(grandTotal)
                            : "---"}
                        </td>
                        <td>{new Date(form.createdAt).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="dashboard-sidebar-content">
          <div className="card">
            <h3 className="card-title">Broker Performance</h3>
            {analytics.brokerPerformance.length > 0 ? (
              <BrokerPerformanceChart
                performanceData={analytics.brokerPerformance}
              />
            ) : (
              <p className="empty-state">No submitted forms to analyze.</p>
            )}
          </div>
        </aside>
      </main>
    </div> 
  )
}

export default AmanagerDashboard;